import * as React from "react";
import { RotateCw } from "lucide-react";

import { usePortal } from "@/features/portal/portal-context";
import { BrandPreviewFrame, type ThemePreview } from "@/features/preview/brand-preview-frame";
import { useThemeStudioOptional } from "@/features/theming/theme-studio-context";

/** Sticky preview column shared by the Theming and Terminology pages. Embeds the
 *  real pop.in app for the active brand at mobile size and pushes the *unsaved*
 *  draft theme into it live (no save/reload) as the partner edits. The initial
 *  frame loads the published theme; "Save & publish" persists, and Reload forces
 *  a fresh load. */
export function LivePreview() {
  const { activeBrand, previewReloadKey, draft, setPinVerdicts, setPinAwaiting } = usePortal();
  // The Theme Studio (present only on the Theming page). When the user has
  // engaged its knobs/candidates, forward the RECIPE so a v2-aware embed
  // regenerates the full theme with the real engine; otherwise send 6-hex only.
  const studio = useThemeStudioOptional();
  const recipe = studio?.recipeActive ? studio.recipe : null;
  // Local nudge layered on top of the publish-driven key so the manual Reload
  // button can force a fresh load without a publish.
  const [nudge, setNudge] = React.useState(0);

  // The live draft pushed into the embedded app on every edit.
  const preview = React.useMemo<ThemePreview>(
    () => ({
      name: draft.theme.name,
      colors: {
        background: draft.theme.background,
        surface: draft.theme.surface,
        primary: draft.theme.primary,
        accent: draft.theme.accent,
        text: draft.theme.text,
        header: draft.theme.header,
      },
      logoText: draft.theme.logoText,
      logoImg: draft.theme.logoImg,
      terminology: {
        currencySingular: draft.terms.currencySingular,
        currencyPlural: draft.terms.currencyPlural,
        currencyIcon: draft.terms.currencyIcon,
        walletLabel: draft.terms.walletLabel,
        rewardLabel: draft.terms.rewardLabel,
      },
      ...(recipe
        ? {
            recipe: {
              treatment: recipe.treatmentId,
              seed: recipe.seed,
              polarity: recipe.polarity,
              fontId: recipe.fontId,
            },
          }
        : {}),
    }),
    [draft, recipe],
  );

  if (!activeBrand) return null;

  return (
    <div className="sticky top-6">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-[.06em] text-text-3">Live preview</div>
        <button
          type="button"
          onClick={() => setNudge((n) => n + 1)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-panel-2 px-2.5 py-1 text-[11.5px] font-semibold text-muted-foreground transition-colors hover:text-foreground"
        >
          <RotateCw className="size-3.5" /> Reload
        </button>
      </div>

      <div className="grid place-items-center py-2">
        <BrandPreviewFrame
          brandId={activeBrand.id}
          reloadKey={previewReloadKey + nudge}
          preview={preview}
          onPinVerdicts={setPinVerdicts}
          onPinAwaiting={setPinAwaiting}
        />
      </div>

      <div className="mt-2.5 text-center text-[11.5px] text-text-3">
        Live preview of your unsaved changes. Save &amp; publish to make them
        permanent.
      </div>
    </div>
  );
}
